import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import WavesIcon from '@mui/icons-material/Waves';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import DirectionsIcon from '@mui/icons-material/Directions';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ExploreIcon from '@mui/icons-material/Explore';
import NearMeIcon from '@mui/icons-material/NearMe';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface BeachItem {
  id: string;
  name: string;
  rating: string;
  reviews: number;
  type: string;
  location: string;
  mapUrl?: string;
  imageUrl?: string;
  lat?: number;
  lng?: number;
  distance?: string;
  description?: string;
  color?: string;
}

interface BeachesContent {
  title: string;
  description: string;
  beaches: BeachItem[];
}

const beachGradients = [
  ['#0ea5e9', '#2563eb'],
  ['#06b6d4', '#0891b2'],
  ['#14b8a6', '#0d9488'],
  ['#3b82f6', '#6366f1'],
  ['#8b5cf6', '#7c3aed'],
  ['#ec4899', '#db2777'],
  ['#f59e0b', '#d97706'],
  ['#22c55e', '#16a34a'],
  ['#ef4444', '#dc2626'],
  ['#f97316', '#ea580c'],
];
const templeLat = 11.4806;
const templeLng = 75.5916;

function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number) {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10;
}

const safetyTips = [
  'Swim only in designated areas with lifeguards',
  'Follow lifeguard instructions at all times',
  'Avoid swimming during high tide or rough seas',
  'Don\'t leave valuables unattended on the beach',
  'Use sunscreen and stay hydrated',
  'Keep the beach clean — no littering',
];

export default function Beaches() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<BeachesContent>({
    title: 'Beaches Nearby',
    description: 'Explore beautiful beaches around Muchukunnu',
    beaches: [
      { id: '1', name: 'Thikkodi Drive-in Beach', rating: '4.5', reviews: 745, type: 'Drive-in Beach', location: 'Thikkodi, Kerala', mapUrl: 'https://maps.app.goo.gl/cYlu6oDMVovpOI3RU', distance: '7.1 km', description: 'Unique beach where vehicles can drive near shore. Distance: 7.1 km, Travel time: 18 minutes. Route: Muchukunnu → Purakkad Road → Thikkodi.' },
      { id: '2', name: 'Payyoli Beach', rating: '4.1', reviews: 152, type: 'Beach', location: 'Payyoli, Kerala', mapUrl: 'https://maps.app.goo.gl/payyoli' },
    ]
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'beaches'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as BeachesContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading beaches:', error);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const beaches = content.beaches.map((b, i) => ({
    ...b,
    gradient: beachGradients[i % beachGradients.length],
  }));

  return (
    <Box sx={{ bgcolor: '#eef7fb', minHeight: '100vh' }}>
      <SEO title="Beaches Near Kottayil Temple | സമീപ ബീച്ചുകൾ" description="Beautiful beaches near Kottayil Temple (Kotta-Kovilakam Kshethram) — Kappad Beach, Kozhikode Beach, Payyoli Beach and other Kerala coastal attractions." path="/nearby/beaches" keywords="beaches near Kottayil temple, Kozhikode beaches, Kappad Beach, Kerala beaches, Payyoli beach, കോഴിക്കോട് ബീച്ച്, കാപ്പാട് ബീച്ച്, beaches near Muchukunnu" />
      <PageBanner title="Beaches Near Temple" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO SECTION */}
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(7, 63, 97, 0.2)',
                background: 'linear-gradient(145deg, #0b496d 0%, #0a6f93 48%, #1fa6d0 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box sx={{ position: 'absolute', top: -56, right: -56, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -48, left: -48, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box sx={{
                    width: 66, height: 66, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.25)',
                    boxShadow: '0 12px 30px rgba(9,40,58,0.28)',
                  }}>
                    <BeachAccessIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontWeight: 900, fontSize: { xs: '1.55rem', sm: '2rem' },
                      color: '#fff', lineHeight: 1.15, mb: 0.5,
                      textShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    }}>
                      Coastal Beaches Nearby
                    </Typography>
                    <Typography sx={{
                      fontSize: { xs: '0.82rem', sm: '0.9rem' },
                      color: 'rgba(255,255,255,0.68)', fontWeight: 500,
                    }}>
                      Drive-in, family-friendly, and scenic beach spots
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{
                  fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.58)',
                  lineHeight: 1.9, maxWidth: 560, mb: 2.5,
                }}>
                  From the famous drive-in beach to quieter coastal stretches, plan your beach visits with quick access to location details and directions.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip icon={<ExploreIcon sx={{ fontSize: '16px !important', color: '#0f5b8a !important' }} />} label={`${beaches.length} Beach${beaches.length !== 1 ? 'es' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#0e527c', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<WavesIcon sx={{ fontSize: '16px !important', color: '#bfdbfe !important' }} />} label="Shoreline Experiences" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.18)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.3 }}>
                  {[
                    { label: 'Beach Directory', value: `${beaches.length}`, icon: BeachAccessIcon },
                    { label: 'Travel Ready', value: 'Map & Distance', icon: NearMeIcon },
                    { label: 'Safety Focus', value: 'Coastal Guidelines', icon: HealthAndSafetyIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box key={metric.label} sx={{ borderRadius: '14px', p: 1.4, bgcolor: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.16)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>{metric.value}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* BEACH CARDS */}
            {beaches.length === 0 ? (
              <Box sx={{ bgcolor: '#fff', borderRadius: '24px', p: 6, textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <WavesIcon sx={{ fontSize: 56, color: '#e0e0e0', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1rem' }, color: '#bbb', fontWeight: 600 }}>Beach information coming soon.</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                {beaches.map((b, idx) => {
                  const dist = b.distance || (b.lat && b.lng ? `${getDistanceKm(templeLat, templeLng, b.lat, b.lng)} km` : '');
                  const mapsUrl = b.mapUrl || (b.lat && b.lng ? `https://www.google.com/maps/dir/?api=1&origin=${templeLat},${templeLng}&destination=${b.lat},${b.lng}` : '#');
                  const [gradStart, gradEnd] = b.gradient;
                  return (
                    <Box key={b.id || b.name} sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(15,95,145,0.14)',
                      boxShadow: '0 4px 24px rgba(9,69,106,0.1)',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 20px 60px rgba(9,69,106,0.2), 0 0 0 1px ${gradStart}30`,
                      },
                    }}>
                      {/* Image or gradient banner */}
                      <Box sx={{ position: 'relative' }}>
                        {b.imageUrl ? (
                          <Box sx={{
                            width: '100%', height: { xs: 200, sm: 260 }, overflow: 'hidden',
                            position: 'relative',
                          }}>
                            <img
                              src={b.imageUrl} alt={b.name}
                              style={{
                                width: '100%', height: '100%', objectFit: 'cover',
                                transition: 'transform 600ms ease',
                              }}
                              onMouseOver={(e) => { (e.target as HTMLImageElement).style.transform = 'scale(1.05)'; }}
                              onMouseOut={(e) => { (e.target as HTMLImageElement).style.transform = 'scale(1)'; }}
                            />
                            {/* Gradient overlay on image */}
                            <Box sx={{
                              position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%',
                              background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)',
                            }} />
                            {/* Title overlay on image */}
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 2, sm: 2.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                  width: 42, height: 42, borderRadius: '14px',
                                  background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  boxShadow: `0 6px 20px ${gradStart}50`,
                                }}>
                                  <WavesIcon sx={{ fontSize: 22, color: '#fff' }} />
                                </Box>
                                <Box>
                                  <Typography sx={{
                                    fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    lineHeight: 1.2,
                                  }}>
                                    {b.name}
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)',
                                    fontWeight: 500,
                                  }}>
                                    {b.location}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          /* No-image gradient header */
                          <Box sx={{
                            background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`,
                            px: { xs: 2.5, sm: 3 }, py: { xs: 2.5, sm: 3 },
                            position: 'relative', overflow: 'hidden',
                          }}>
                            <Box sx={{
                              position: 'absolute', top: -20, right: -20, width: 100, height: 100,
                              borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
                            }} />
                            <Box sx={{
                              position: 'absolute', bottom: -30, left: '50%', width: 140, height: 140,
                              borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
                            }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box sx={{
                                width: 46, height: 46, borderRadius: '14px',
                                background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid rgba(255,255,255,0.25)',
                              }}>
                                <WavesIcon sx={{ fontSize: 24, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{
                                  fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.2rem' },
                                  color: '#fff', lineHeight: 1.2,
                                }}>
                                  {b.name}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500,
                                }}>
                                  {b.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Card body */}
                      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, bgcolor: '#f8fcff' }}>
                        {/* Info chips */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          {dist && (
                            <Chip
                              icon={<NearMeIcon sx={{ fontSize: '15px !important', color: `${gradStart} !important` }} />}
                              label={`${dist} from temple`}
                              size="small"
                              sx={{
                                bgcolor: `${gradStart}10`, color: gradStart,
                                fontWeight: 700, fontSize: '0.75rem', height: 30,
                                borderRadius: '10px', border: `1px solid ${gradStart}20`,
                                '& .MuiChip-icon': { ml: '6px' },
                              }}
                            />
                          )}
                          <Chip
                            icon={<LocationOnIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />}
                            label={b.type}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(100,116,139,0.08)', color: '#64748b',
                              fontWeight: 600, fontSize: '0.75rem', height: 30,
                              borderRadius: '10px', border: '1px solid rgba(100,116,139,0.12)',
                              '& .MuiChip-icon': { ml: '6px' },
                            }}
                          />
                          {!b.imageUrl && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />}
                              label={`${b.rating} (${b.reviews} reviews)`}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(245,158,11,0.08)', color: '#d97706',
                                fontWeight: 600, fontSize: '0.75rem', height: 30,
                                borderRadius: '10px', border: '1px solid rgba(245,158,11,0.12)',
                                '& .MuiChip-icon': { ml: '6px' },
                              }}
                            />
                          )}
                        </Box>

                        {/* Description */}
                        {b.description && (
                          <Typography sx={{
                            fontSize: '0.88rem', color: '#4f677c', lineHeight: 1.8, mb: 2.5,
                            letterSpacing: '0.01em',
                          }}>
                            {b.description}
                          </Typography>
                        )}

                        {/* Directions button */}
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 1,
                            px: 2.5, py: 1, borderRadius: '14px',
                            background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})`,
                            color: '#fff', transition: 'all 300ms ease',
                            boxShadow: `0 4px 16px ${gradStart}30`,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 28px ${gradStart}40`,
                            },
                          }}>
                            <DirectionsIcon sx={{ fontSize: 18 }} />
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.02em' }}>
                              Get Directions
                            </Typography>
                          </Box>
                        </a>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* SAFETY TIPS FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #103447 0%, #19536f 45%, #0c2632 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}>
              <Box sx={{
                position: 'absolute', inset: 0, opacity: 0.06,
                backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)',
              }} />
              <Box sx={{
                position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%',
                background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)`,
              }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #b8941f)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 6px 20px ${GOLD}30`,
                  }}>
                    <HealthAndSafetyIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>
                      Beach Safety Tips
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
                      Stay safe while enjoying the coast
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  {safetyTips.map((t, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1,
                        px: 1.5, borderRadius: '10px',
                        transition: 'all 250ms ease',
                        border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                      }}>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '7px',
                          bgcolor: `${GOLD}20`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', flexShrink: 0, mt: 0.1,
                        }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 900, color: GOLD }}>
                            {i + 1}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>
                          {t}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/beaches" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
